/**
 * Client mirror of the assumption-acknowledgment contract (S4d).
 *
 * TYPES ONLY, and deliberately NO VALUES. The reference numbers live on the
 * server and are fetched from `GET /api/action-plan/reference-assumptions`.
 *
 * A client-side copy is exactly what made the old form dangerous: it pre-filled
 * 60 datasets / $150 per hour / team of 10 as ANSWERS, the server held its own
 * identical defaults, and neither side could tell the user which of the two
 * produced the figure on screen. Duplicating them here would also let the two
 * drift, at which point a `reference_default` acknowledgment would assert a
 * citation for a number the server never published — and the server rejects
 * exactly that, so the drift would surface as an unexplained 400.
 *
 * Held identical to the server by
 * `client/types/__tests__/actionPlanValue.parity.test.ts`.
 */

import type { ValueProvenance } from './actionPlanValueProvenance';

/** The inputs a plan's financial model can consume. */
export const ASSUMPTION_KEYS = [
  'dataset_count',
  'hourly_rate',
  'team_size',
  'industry',
  'organization_size',
] as const;

export type AssumptionKey = (typeof ASSUMPTION_KEYS)[number];

/**
 * Source metadata that makes a reference value checkable. Every field required.
 *
 * Mirrors the server; see `ReferenceSource` there for why each one is mandatory.
 */
export interface ReferenceSource {
  document: string;
  locator: string;
  published: string;
  cohort: string;
  method: string;
}

/**
 * A cited reference value the user may accept. Fetched, never hardcoded.
 *
 * The catalog is currently EMPTY: the first draft published 60 / $150 / 10 with
 * prose that had the grammar of a citation and none of the substance, and those
 * entries were withdrawn rather than reworded. The form therefore renders no
 * "Use reference value" action, and a user's own figure is the only way an input
 * becomes available.
 */
export interface ReferenceAssumption {
  key: AssumptionKey;
  value: number | string;
  unit: string;
  basis: string;
  source: ReferenceSource;
  version: string;
}

export interface ReferenceAssumptionCatalog {
  version: string;
  assumptions: ReferenceAssumption[];
}

/**
 * One acknowledged assumption, as submitted.
 *
 * A DISCRIMINATED UNION, mirroring the server: `reference_version` is REQUIRED on
 * a reference acceptance and FORBIDDEN on the user's own figure. It was previously
 * an optional field on a single shape, with the version check running only when
 * present — so a caller could omit it and bypass stale-catalog protection while
 * the comment claimed it was required.
 */
export type AcknowledgedAssumption =
  | {
      key: AssumptionKey;
      value: number | string;
      basis: 'user_supplied';
      reference_version?: never;
    }
  | {
      key: AssumptionKey;
      value: number | string;
      basis: 'reference_default';
      /** Which catalog version was on screen. Checked against the current one. */
      reference_version: string;
    };

/** What a plan was priced on, echoed back for the confirmation summary. */
export interface AssumptionSummaryEntry {
  key: AssumptionKey;
  value: number | string | null;
  provenance: ValueProvenance;
  basis: string;
}

/** Field labels. Copy, not data — safe to hold on the client. */
export const ASSUMPTION_LABEL: Record<AssumptionKey, string> = {
  dataset_count: 'Number of datasets',
  hourly_rate: 'Loaded hourly rate',
  team_size: 'Team size',
  industry: 'Industry segment',
  organization_size: 'Organization size',
};

/**
 * The keys without which no value can be computed.
 *
 * Mirrors `VALUE_CRITICAL_ASSUMPTIONS` on the server. The others only tune
 * multipliers, so their absence costs precision rather than the whole model.
 */
export const VALUE_CRITICAL_ASSUMPTIONS: readonly AssumptionKey[] = [
  'dataset_count',
  'hourly_rate',
];
